import React, { useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import TabContext from '@material-ui/lab/TabContext'
import Typography from '@material-ui/core/Typography'
import { Tab, TabList } from 'components/styled'
import useRoutedTabs, { TabList as TabListType } from 'hooks/useRoutedTabs'
import { PageRecordItemContent } from 'components/content'

import BackButton from 'components/backButton'

import { sliceUrl } from 'utils'

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 0
  },
  alignItemsCenter: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1, 0)
  },
  subtitle: {
    textTransform: 'uppercase',
    color: theme.palette.inputs.label.color.regular,
    fontWeight: theme.palette.inputs.label.semibold,
    fontSize: '0.875rem'
  },
  title: {
    fontWeight: theme.palette.inputs.label.semibold
  },
  tabWrapper: {
    maxWidth: '100%'
  },
  contentContainer: {
    flexGrow: 1,
    display: 'flex',
    padding: 0,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 3)
    }
  },
  tabs: {
    color: theme.palette.grey[400],
    '& .Mui-selected': {
      color: theme.palette.common.white
    },
    '& > div': {
      overflowX: 'unset !important'
    }
  }
}))

interface EntityCardProps {
  title: string
  subtitle?: string
  tabs?: TabListType
  tabKey?: string
  actionsComponent?: JSX.Element
  entity?: any
}

export const AuthorisableEntityContext = React.createContext<{ entity: any }>(null as any)

const ItemCard: React.FC<EntityCardProps> = ({ title, subtitle, tabs, tabKey, children, actionsComponent, entity }) => {
  const classes = useStyles()

  const { url } = useRouteMatch()

  const { value, handleTabChange } = useRoutedTabs(tabs, { tabKey })

  const back = useMemo(() => sliceUrl(url, 1), [url])

  return (
    <AuthorisableEntityContext.Provider value={{ entity }}>
      <PageRecordItemContent>
        <TabContext value={value ?? '/'}>
          <Container maxWidth={false} className={classes.headerContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid container item xs={12} md={12} className={classes.alignItemsCenter}>
                    <Grid container item>
                      <Grid item xs={12}>
                        <Grid container spacing={3} alignItems='center'>
                          <Grid item>
                            <BackButton to={back} />
                          </Grid>
                          <Grid item xs={9}>
                            <Grid container direction='column'>
                              {subtitle !== undefined && (
                                <Grid item>
                                  <Typography variant='subtitle1' component='h3' className={classes.subtitle}>
                                    {subtitle}
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item>
                                <Typography variant='h4' component='h1' className={classes.title}>
                                  {title}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item>{actionsComponent}</Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {tabs !== undefined && (
                <Grid item className={classes.tabWrapper}>
                  <TabList onChange={handleTabChange} scrollButtons='auto' className={classes.tabs}>
                    {tabs.map(({ label, path, icon }) => (
                      <Tab key={path} label={label} value={path} icon={icon} />
                    ))}
                  </TabList>
                </Grid>
              )}
            </Grid>
          </Container>
          <Container maxWidth={false} className={classes.contentContainer}>
            {children}
          </Container>
        </TabContext>
      </PageRecordItemContent>
    </AuthorisableEntityContext.Provider>
  )
}

export default ItemCard
